import { createContext, useContext, useCallback, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { authApi } from "@/api/endpoints/auth.js";
import { onSessionExpired } from "@/api/client.js";

/**
 * Sessão do usuário.
 *
 * A verdade sobre quem está logado vem sempre do servidor (`GET /auth/me`),
 * validando o cookie httpOnly. O app antigo lia um JSON do `localStorage`,
 * que qualquer pessoa podia editar no DevTools para virar "master".
 */

const AuthContext = createContext(null);

const CHAVE_SESSAO = ["auth", "me"];

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: CHAVE_SESSAO,
    queryFn: async () => {
      try {
        return await authApi.me();
      } catch (erro) {
        if (erro.status === 401) return { user: null, impersonating: null };
        throw erro;
      }
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  /** Guarda a sessão retornada pelo login sem precisar de novo round-trip. */
  const setSession = useCallback(
    (user) => queryClient.setQueryData(CHAVE_SESSAO, { user, impersonating: null }),
    [queryClient],
  );

  const clearSession = useCallback(() => {
    queryClient.setQueryData(CHAVE_SESSAO, { user: null, impersonating: null });
    // Descarta os dados das telas para não vazar informação entre contas.
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== "auth" });
  }, [queryClient]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      clearSession();
    }
  }, [clearSession]);

  const refresh = useCallback(
    () => queryClient.invalidateQueries({ queryKey: CHAVE_SESSAO }),
    [queryClient],
  );

  // Qualquer 401 vindo de qualquer tela derruba a sessão local uma vez só.
  useEffect(() => onSessionExpired(clearSession), [clearSession]);

  const valor = useMemo(
    () => ({
      user: data?.user ?? null,
      impersonating: data?.impersonating ?? false,
      isAuthenticated: Boolean(data?.user),
      isLoading,
      isError,
      setSession,
      clearSession,
      logout,
      refresh,
    }),
    [data, isLoading, isError, setSession, clearSession, logout, refresh],
  );

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const contexto = useContext(AuthContext);
  if (!contexto) throw new Error("useAuth precisa estar dentro de <AuthProvider>.");
  return contexto;
}
